import { useCallback, useState } from 'react';
import { useNotifications } from '../../../hooks/useNotifications';
import { IncidentsService } from '../../../services/incidentsService';

const STATUS_CHANGE_NOT_ALLOWED = 'STATUS_CHANGE_NOT_ALLOWED';

export function useStatusChangeActions() {
  const { showErrorNotification, showSuccessNotification, showConfirmNotification } = useNotifications();
  const [isLoading, setIsloading] = useState(false);

  const handleStatusChangeError = useCallback(
    (error) => {
      if (error?.message === STATUS_CHANGE_NOT_ALLOWED) {
        showErrorNotification({
          title: 'error',
          description: 'statusChangeNotAllowed',
        });
        return;
      }
      showErrorNotification({
        title: 'error',
        description: 'errorUpdatingRecord',
      });
    },
    [showErrorNotification],
  );

  const cancelServiceById = useCallback(
    async ({ serviceId, cancellationReasson }) => {
      setIsloading(true);
      try {
        await IncidentsService.cancel(serviceId, cancellationReasson);
        showSuccessNotification({
          title: 'success',
          description: 'successCancelingService',
        });
        return true;
      } catch (error) {
        handleStatusChangeError(error?.response.data);
        return false;
      } finally {
        setIsloading(false);
      }
    },
    [handleStatusChangeError, showSuccessNotification],
  );

  const finishServiceById = useCallback(
    async (serviceId) => {
      try {
        await IncidentsService.finish(serviceId);
        showSuccessNotification({
          title: 'success',
          description: 'successFinishingService',
        });
        return true;
      } catch (error) {
        handleStatusChangeError(error?.response.data);
        return false;
      }
    },
    [handleStatusChangeError, showSuccessNotification],
  );

  const cancelService = useCallback(
    (serviceId) =>
      new Promise((resolve) => {
        showConfirmNotification({
          title: 'areYouSure',
          content: 'serviceWillBeCanceled',
          onOk: () => {
            const success = cancelServiceById(serviceId);
            resolve(success);
          },
          onCancel: () => resolve(false),
        });
      }),
    [],
  );

  const finishService = useCallback(
    (serviceId) =>
      new Promise((resolve) => {
        showConfirmNotification({
          title: 'areYouSure',
          content: 'serviceWillBeFinished',
          onOk: async () => {
            const success = await finishServiceById(serviceId);
            resolve(success);
          },
          onCancel: () => resolve(false),
        });
      }),
    [finishServiceById],
  );

  const startService = useCallback(
    async (serviceId) => {
      setIsloading(true);
      try {
        await IncidentsService.startService(serviceId);
        showSuccessNotification({
          title: 'success',
          description: 'successStartingService',
        });
        return true;
      } catch (error) {
        handleStatusChangeError(error?.response.data);
        return false;
      } finally {
        setIsloading(false);
      }
    },
    [],
  );

  return {
    finishService,
    cancelService,
    cancelServiceById,
    isLoading,
    startService,
  };
}
